export const images = {
  mainMockup: "https://cdn.magicpatterns.com/uploads/bhuG6mGtcaCij7HZzMYVWV/AROMALAB-EN.png",
  author: "https://cdn.magicpatterns.com/uploads/w3Vk5bLzR29NzyNRDFugpz/NANCY_URIBE_-_AROMAS.png",
};

export const flowSteps = ["Idea", "Formula", "Experiment", "Product", "Collection"];

export const painPoints = [
  "Maybe you have ingredients sitting around, but you're not sure what works well together.",
  "Maybe you've spent hours searching Pinterest, Google, YouTube or social media for recipes.",
  "Maybe you've tried experimenting… only to waste ingredients because the result wasn't what you expected.",
  "Or maybe you've already created a few products, but they all feel disconnected from each other.",
];

export const beforeItems = [
  "You have ingredients, but you're not sure how to combine them.",
  "You spend too much time searching for recipes and inspiration.",
  "Every new product starts from scratch.",
  "You waste materials testing combinations that don't work.",
  "You have lots of ideas but no clear process for developing them.",
  "Your products don't yet feel like part of a cohesive collection.",
];

export const afterItems = [
  "Explore different aromatic families and profiles.",
  "Combine elements intentionally.",
  "Create variations from a base formula.",
  "Record your experiments.",
  "Learn from each test.",
  "Develop names and concepts for your creations.",
  "Organize products into cohesive collections.",
  "Build a product library you can continue expanding.",
];

export const volumes = [
  {
    numeral: "I",
    title: "The Aroma Map",
    color: "#C9D4BC",
    body: "Before you start creating, you need to understand what you're working with. I'll walk you through different aromatic families, profiles, characteristics and intensity levels so you can start making more intentional decisions instead of combining ingredients randomly.",
    closing: "You'll learn how to think about aromas as building blocks.",
    listType: "none",
    list: [] as string[],
  },
  {
    numeral: "II",
    title: "The Aroma Combination Matrix",
    color: "#EBCDBE",
    body: "This is where things start getting interesting. I'll show you how to use a simple combination framework to generate new possibilities without having to search for another recipe every time you want to create something different. You'll learn how to explore:",
    closing: "The goal isn't to memorize combinations. The goal is to understand how to CREATE them.",
    listType: "chips",
    list: [
      "Complementary profiles",
      "Contrasting profiles",
      "Supporting notes",
      "Dominant notes",
      "Fresh combinations",
      "Floral combinations",
      "Herbal combinations",
      "Woody combinations",
      "Citrus combinations",
      "Spiced combinations",
    ],
  },
  {
    numeral: "III",
    title: "The Formulation Method",
    color: "#BFCDB5",
    body: "Now we're going to take your ideas and turn them into documented formulas. I'll show you how to:",
    closing: "So when you discover something you love… you won't have to rely on memory. You'll have a process for documenting and improving it.",
    listType: "steps",
    list: [
      "Start with a concept.",
      "Select your aromatic direction.",
      "Build a base formula.",
      "Record your ingredients.",
      "Test your combination.",
      "Document what happened.",
      "Make adjustments.",
      "Create variations.",
    ],
  },
  {
    numeral: "IV",
    title: "The Collection Builder",
    color: "#E6C6D0",
    body: "Creating one product is one thing. Creating a collection is something completely different. I'll show you how to take several individual creations and organize them around:",
    closing: "Instead of having a random list of products… you'll start thinking like a product creator.",
    listType: "checks",
    list: [
      "A central concept.",
      "A recognizable style.",
      "Related aromatic profiles.",
      "Product names.",
      "Variations.",
      "A consistent collection theme.",
    ],
  },
  {
    numeral: "V",
    title: "From Aroma to Product",
    color: "#E4E1D2",
    body: "A formula isn't automatically a product. You need to give it an identity. Inside this volume, I'll show you how to turn each creation into a more complete product concept with:",
    closing: "You'll go from “I made this formula.” to “This is my product.”",
    listType: "checks",
    list: [
      "A product name.",
      "A central concept.",
      "An aromatic profile.",
      "A product description.",
      "A presentation concept.",
      "A product story.",
      "A place within your collection.",
    ],
  },
  {
    numeral: "VI",
    title: "Build Your First Collection",
    color: "#CFC8E6",
    body: "This is where everything comes together. I'll guide you through selecting your strongest creations, organizing them into a cohesive collection, and preparing your first product line.",
    closing: "You don't need 50 products. You don't need hundreds of formulas. I want you to learn how to create a focused collection with intention.",
    listType: "none",
    list: [] as string[],
  },
];

export const resources = [
  { title: "Formulation Worksheets", body: "Use them to document your ideas, ingredients, experiments, modifications and results.", icon: "flask" },
  { title: "Combination Matrices", body: "Use structured frameworks to generate new product ideas.", icon: "grid" },
  { title: "Product Development Sheets", body: "Turn your formulas into organized product concepts.", icon: "file" },
  { title: "Collection-Building Frameworks", body: "Organize individual products into cohesive collections.", icon: "library" },
  { title: "Experimentation Systems", body: "Track what you tested, what changed and what you learned.", icon: "microscope" },
];

export const bonuses = [
  {
    number: 1,
    title: "100 Names for Your Aromas",
    value: 37,
    image: "https://cdn.magicpatterns.com/uploads/9hdnyLK4DUbDmeB8gmgpi2/BONUS_1_AROMALAB-EN.png",
    paragraphs: [
      "Have you ever created something you love… and then spent forever trying to figure out what to call it?",
      "I created this library so you don't have to start from a blank page every time. You'll get 100 name ideas organized around different styles and concepts to help you name your creations faster and give your products a more memorable identity.",
    ],
    listType: "none",
    list: [] as string[],
    closing: "",
  },
  {
    number: 2,
    title: "The Aroma Combination Atlas",
    value: 47,
    image: "https://cdn.magicpatterns.com/uploads/2hS3HvDhJqo1jGHedDMmCg/BONUS_2_AROMALAB-EN.png",
    paragraphs: [
      "This is your additional reference guide for exploring new aromatic combinations. Inside you'll find organized combination ideas across different aromatic families and styles, including:",
    ],
    listType: "chips",
    list: ["Fresh", "Floral", "Herbal", "Woody", "Citrus", "Spiced", "Warm", "Soft", "Bold", "Balanced"],
    closing: "Use it whenever you're stuck. Instead of asking “What should I make?” — you'll have a structured starting point.",
  },
  {
    number: 3,
    title: "50 Concepts to Create Product Collections",
    value: 37,
    image: "https://cdn.magicpatterns.com/uploads/kvq784oZgjMaEwtJY98mfE/BONUS_3_AROMALAB-EN.png",
    paragraphs: [
      "Sometimes the hardest part isn't creating the formula. It's deciding: “What should my collection actually be about?”",
      "So I created 50 collection concepts you can use as inspiration for developing new product lines. Each concept gives you a direction you can build around.",
    ],
    listType: "none",
    list: [] as string[],
    closing: "This makes it much easier to go from ONE PRODUCT to A COHESIVE COLLECTION.",
  },
  {
    number: 4,
    title: "The Aroma Experimentation Journal",
    value: 29,
    image: "https://cdn.magicpatterns.com/uploads/asHb2qLHrEcvKkjq9pRa3k/BONUS_4_AROMALAB-EN.png",
    paragraphs: [
      "I don't want you experimenting randomly and then forgetting what you did. This journal gives you a structured place to document your creative process. You'll be able to record:",
    ],
    listType: "checks",
    list: [
      "Your original idea",
      "Ingredients",
      "Formula",
      "First test",
      "Changes",
      "Observations",
      "Results",
      "What you'd change next time",
      "Final version",
      "Product name",
      "Collection concept",
    ],
    closing: "Think of it as your personal Aroma Lab notebook.",
  },
  {
    number: 5,
    title: "30 Days to Your First Collection",
    value: 37,
    image: "https://cdn.magicpatterns.com/uploads/vZRjFdT3JuyFWEkY7xGAsc/BONUS_5_AROMALAB-EN.png",
    paragraphs: [
      "And if you're thinking: “Nancy, this all sounds great… but where do I actually start?” — I created this for you.",
      "You'll get a simple 30-day roadmap that takes you through the process one step at a time.",
    ],
    listType: "days",
    list: [
      "Choose your collection concept.",
      "Explore your aromatic direction.",
      "Select your first combination.",
      "Create your first formula.",
      "Document your experiment.",
      "Review your results.",
      "Create your first variation.",
    ],
    closing: "And from there, you continue building until you have the foundations of your first collection.",
  },
];

export const offerStack = [
  { label: "The Aroma Lab™ — 6 Premium Volumes", value: 97, main: true },
  { label: "Bonus #1 · 100 Names for Your Aromas", value: 37, main: false },
  { label: "Bonus #2 · The Aroma Combination Atlas", value: 47, main: false },
  { label: "Bonus #3 · 50 Collection Concepts", value: 37, main: false },
  { label: "Bonus #4 · The Aroma Experimentation Journal", value: 29, main: false },
  { label: "Bonus #5 · 30 Days to Your First Collection", value: 37, main: false },
];

export const testimonials = [
  {
    name: "Emily R.",
    role: "Candle maker · Portland, OR",
    image: "https://cdn.magicpatterns.com/patterns/generated-images/fd4d5c75-b087-417d-9a01-c5790a39bb99.jpg",
    quote: "I used to spend whole evenings on Pinterest looking for the “perfect” recipe. Now I open the Combination Matrix and have three new ideas in ten minutes. I just finished a 4-scent line that finally feels like me.",
  },
  {
    name: "Sarah M.",
    role: "Handmade soap seller · Leeds, UK",
    image: "https://cdn.magicpatterns.com/patterns/generated-images/e72329f3-1242-41ec-be46-7fe7ec0ebafb.jpg",
    quote: "The Experimentation Journal alone was worth it. I stopped losing my best blends because I couldn't remember what I did. Every test is documented, and every variation builds on the last one.",
  },
  {
    name: "Danielle K.",
    role: "Complete beginner · Atlanta, GA",
    image: "https://cdn.magicpatterns.com/patterns/generated-images/96d8eebb-3b70-4cfe-8238-21f7b0277588.jpg",
    quote: "I had zero experience. The Aroma Map made aromatic families actually make sense. For the first time I'm combining things on purpose instead of guessing and wasting ingredients.",
  },
  {
    name: "Marcus T.",
    role: "Workshop owner · Toronto, CA",
    image: "https://cdn.magicpatterns.com/patterns/generated-images/f56abd24-dca5-4852-96c3-057fd0155158.jpg",
    quote: "I had twelve products that had nothing to do with each other. The Collection Builder helped me turn them into two real collections with names, stories and a clear style. Customers noticed.",
  },
  {
    name: "Linda P.",
    role: "Hobby creator · Brisbane, AU",
    image: "https://cdn.magicpatterns.com/patterns/generated-images/0cb7ab20-8dda-4b34-a3eb-81463dce8cf2.jpg",
    quote: "I bought it for the price and stayed for the system. The 30-day roadmap kept me moving one small step at a time. I've never finished a creative project this fast.",
  },
];

export const faqs = [
  { q: "Do I need previous experience?", a: "No. The system is designed to take you from the fundamentals through the process of developing formulas and organizing your first collection." },
  { q: "Do I need to already have a business?", a: "Not at all. You can use The Aroma Lab whether you're simply exploring product creation or you're already selling handmade products." },
  { q: "Do I need special ingredients?", a: "Not necessarily. The system focuses on the creative and organizational process. The ingredients you choose will depend on the type of aromatic products you want to develop and the applicable practices and regulations in your location." },
  { q: "Is this just a recipe book?", a: "No. That's actually the main difference. The goal is to help you develop a repeatable creative system, rather than making you permanently dependent on other people's recipes." },
  { q: "How will I receive the materials?", a: "After completing your purchase, you'll receive instructions for accessing your digital materials." },
  { q: "Is this a one-time payment?", a: "Yes. You pay $27 once. There are no monthly subscription fees." },
  { q: "How long will I have access?", a: "You'll have lifetime access to the materials included with your purchase, subject to the applicable offer terms." },
  { q: "What if I decide it's not for me?", a: "You have a 7-day guarantee. If you meet the applicable guarantee conditions, you can request a refund within that period." },
];

export const recentBuyers = [
  { name: "María G.", country: "Mexico" },
  { name: "Valentina R.", country: "Colombia" },
  { name: "Lucía M.", country: "Spain" },
  { name: "Camila S.", country: "Argentina" },
  { name: "Sofía P.", country: "Chile" },
  { name: "Isabella T.", country: "Peru" },
  { name: "Daniela V.", country: "Ecuador" },
  { name: "Gabriela L.", country: "Venezuela" },
  { name: "Carmen D.", country: "Dominican Republic" },
  { name: "Ana F.", country: "Uruguay" },
  { name: "Paola C.", country: "Guatemala" },
  { name: "Fernanda A.", country: "Costa Rica" },
  { name: "Andrea N.", country: "Puerto Rico" },
  { name: "Mariana H.", country: "Panama" },
  { name: "Rosa E.", country: "Bolivia" },
];
